import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useDatabase } from '../context/DatabaseContext';
import DesignChatPanel, {
    DesignChatReportSelector,
    buildDesignChatReportOptions,
} from '../components/reports/DesignChatPanel';
import { Card } from '../components/ui/Card';
import { SegmentedControl } from '../components/ui/SegmentedControl';
import {
    GeneratedReportDashboard,
    GeneratedOverallDashboard,
} from '../reports/reportDashboardRegistry.generated';
import {
    getGeneratedReportUIManifest,
    getReportGeneration,
    getReportsForDatabase,
} from '../services/api';


const TIME_FRAME_LABEL_KEYS = {
    yesterday: 'reportChat.timeFrames.yesterday',
    week_to_date: 'reportChat.timeFrames.weekToDate',
    last_week: 'reportChat.timeFrames.lastWeek',
    month_to_date: 'reportChat.timeFrames.monthToDate',
    last_month: 'reportChat.timeFrames.lastMonth',
    custom: 'reportChat.timeFrames.customRange',
};


function normalizeExecutedQueries(executedQueries) {
    if (!Array.isArray(executedQueries)) return [];
    return executedQueries.map((q) => {
        if (!q || typeof q !== 'object') return q;
        let rows = q.result;
        if (typeof rows === 'string') {
            try {
                rows = JSON.parse(rows);
            } catch {
                rows = [];
            }
        }
        if (rows != null && !Array.isArray(rows)) rows = [];
        return { ...q, result: rows ?? [] };
    });
}


function pickLatestTimeFrameReport(reports) {
    if (!Array.isArray(reports) || reports.length === 0) return null;
    const sorted = [...reports].sort((a, b) => {
        const va = Number(a.version) || 0;
        const vb = Number(b.version) || 0;
        if (vb !== va) return vb - va;
        const ta = String(a.updated_at || a.created_at || '');
        const tb = String(b.updated_at || b.created_at || '');
        return tb.localeCompare(ta);
    });
    return sorted[0];
}


const TF_ORDER = ['yesterday', 'week_to_date', 'last_week', 'month_to_date', 'last_month'];
const DEFAULT_TIME_FRAME_TYPE = 'last_week';


/**
 * Build per-time-frame latest snapshots from a loaded ReportGeneration payload.
 * Returns an ordered map keyed by time-frame type. Missing windows are omitted.
 */
function buildTimeFrameMap(reportData) {
    const byType = reportData?.time_frame_reports_by_type;
    if (!byType || typeof byType !== 'object') return {};
    const out = {};
    for (const tf of TF_ORDER) {
        const arr = byType[tf];
        if (Array.isArray(arr) && arr.length > 0) {
            const latest = pickLatestTimeFrameReport(arr);
            if (latest) out[tf] = latest;
        }
    }
    return out;
}


function firstTypeWithData(tfMap) {
    for (const tf of TF_ORDER) {
        const t = tfMap[tf];
        if (t && Array.isArray(t.executed_queries) && t.executed_queries.length > 0) {
            return tf;
        }
    }
    for (const tf of TF_ORDER) {
        if (tfMap[tf]) return tf;
    }
    return null;
}


/** Prefer Last Week when available; otherwise fall back to the first window with data. */
function defaultTimeFrameType(tfMap) {
    if (tfMap[DEFAULT_TIME_FRAME_TYPE]) {
        return DEFAULT_TIME_FRAME_TYPE;
    }
    return firstTypeWithData(tfMap);
}


export const ReportChat = () => {
    const { t } = useTranslation('pages-misc-5');
    const { databaseId: databaseIdParam } = useParams();
    const databaseId = Number(databaseIdParam);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { databases } = useDatabase();
    const database = useMemo(
        () => databases.find((db) => db.id === databaseId),
        [databases, databaseId],
    );
    const databaseName = database?.name;

    // Initial state is read from URL so a reload preserves mode + selection.
    const [chatMode, setChatMode] = useState(
        () => (searchParams.get('mode') === 'chat' ? 'chat' : 'design'),
    );
    const [reportsList, setReportsList] = useState([]);
    const [manifest, setManifest] = useState(null);
    const [selectedKey, setSelectedKey] = useState(() => searchParams.get('selected'));
    const [remountKey, setRemountKey] = useState(0);
    const autoSelectedReportKeyRef = useRef(null);

    const reportOptions = useMemo(
        () => buildDesignChatReportOptions(manifest, reportsList, databaseId, databaseName),
        [manifest, reportsList, databaseId, databaseName],
    );

    const selected = useMemo(
        () => reportOptions.find((o) => o.value === selectedKey) || null,
        [reportOptions, selectedKey],
    );

    useEffect(() => {
        if (!reportOptions.length) return;
        const overall = reportOptions.find((o) => o.kind === 'overall');
        const defaultKey = overall?.value ?? reportOptions[0].value;

        setSelectedKey((prev) => {
            if (!prev) {
                autoSelectedReportKeyRef.current = defaultKey;
                return defaultKey;
            }
            const stillValid = reportOptions.some((o) => o.value === prev);
            if (!stillValid) {
                autoSelectedReportKeyRef.current = defaultKey;
                return defaultKey;
            }
            // Overall became available after an auto-picked topic default
            if (
                overall &&
                prev === autoSelectedReportKeyRef.current &&
                defaultKey === overall.value &&
                prev !== overall.value
            ) {
                autoSelectedReportKeyRef.current = defaultKey;
                return defaultKey;
            }
            return prev;
        });
    }, [reportOptions]);

    const handleReportSelect = useCallback((key) => {
        autoSelectedReportKeyRef.current = null;
        setSelectedKey(key);
    }, []);

    // Persist current mode + selection to the URL so a hard reload restores the view.
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        if (chatMode === 'chat') next.set('mode', 'chat');
        else next.delete('mode');
        if (selectedKey) next.set('selected', selectedKey);
        else next.delete('selected');
        // Only call when something actually changed to avoid an effect loop.
        if (next.toString() !== searchParams.toString()) {
            setSearchParams(next, { replace: true });
        }
    }, [chatMode, selectedKey, searchParams, setSearchParams]);

    const [topicReportData, setTopicReportData] = useState(null);
    const [topicLoading, setTopicLoading] = useState(false);
    const [topicError, setTopicError] = useState(null);
    const [activeTimeFrameType, setActiveTimeFrameType] = useState(null);

    const [overallReports, setOverallReports] = useState([]);
    const [overallLoading, setOverallLoading] = useState(false);

    // -- Initial fetches ----------------------------------------------------
    const refreshReports = useCallback(async () => {
        if (!databaseId) return;
        try {
            const response = await getReportsForDatabase(databaseId);
            setReportsList(response.data?.data?.reports || []);
        } catch (err) {
            console.error('Failed to load reports list', err);
        }
    }, [databaseId]);

    const refreshManifest = useCallback(async () => {
        if (!databaseId) return null;
        try {
            const res = await getGeneratedReportUIManifest(databaseId);
            const m = res.data?.data ?? { topics: {}, overall: {} };
            setManifest(m);
            return m;
        } catch (err) {
            console.error('Failed to load manifest', err);
            return null;
        }
    }, [databaseId]);

    useEffect(() => {
        refreshReports();
        refreshManifest();
    }, [refreshReports, refreshManifest]);

    // -- Load preview data based on selection -------------------------------
    useEffect(() => {
        let cancelled = false;
        async function loadTopic(reportId) {
            setTopicLoading(true);
            setTopicError(null);
            try {
                const res = await getReportGeneration(reportId);
                if (cancelled) return;
                setTopicReportData(res.data?.data || null);
            } catch (err) {
                if (cancelled) return;
                setTopicError(err?.response?.data?.detail || err?.message || t('reportChat.failedToLoadReport'));
            } finally {
                if (!cancelled) setTopicLoading(false);
            }
        }
        async function loadOverall() {
            setOverallLoading(true);
            const completed = reportsList.filter((r) => r.status === 'completed');
            if (completed.length === 0) {
                setOverallReports([]);
                setOverallLoading(false);
                return;
            }
            try {
                const payloads = [];
                for (const r of completed) {
                    if (cancelled) return;
                    const res = await getReportGeneration(r.id);
                    if (res.data?.data) payloads.push(res.data.data);
                }
                if (!cancelled) setOverallReports(payloads);
            } catch (err) {
                console.error('Failed to load overall reports', err);
                if (!cancelled) setOverallReports([]);
            } finally {
                if (!cancelled) setOverallLoading(false);
            }
        }

        if (!selected) {
            setTopicReportData(null);
            setOverallReports([]);
        } else if (selected.kind === 'topic' && selected.reportId) {
            loadTopic(selected.reportId);
        } else if (selected.kind === 'overall') {
            loadOverall();
        }
        return () => {
            cancelled = true;
        };
    }, [selected, reportsList, t]);

    const handleApplied = useCallback(async () => {
        await refreshManifest();
        // Reload the underlying data for the preview and force a remount so the
        // newly written .tsx module is re-imported.
        if (selected?.kind === 'topic' && selected.reportId) {
            try {
                const res = await getReportGeneration(selected.reportId);
                setTopicReportData(res.data?.data || null);
            } catch (err) {
                console.error('Failed to reload topic preview', err);
            }
        } else if (selected?.kind === 'overall') {
            const completed = reportsList.filter((r) => r.status === 'completed');
            const payloads = [];
            for (const r of completed) {
                try {
                    const res = await getReportGeneration(r.id);
                    if (res.data?.data) payloads.push(res.data.data);
                } catch (err) {
                    console.error('Failed to reload overall preview row', err);
                }
            }
            setOverallReports(payloads);
        }
        setRemountKey((k) => k + 1);
    }, [refreshManifest, selected, reportsList]);

    // -- Compute what to render in the preview pane -------------------------
    const timeFrameMap = useMemo(() => buildTimeFrameMap(topicReportData), [topicReportData]);
    const availableTimeFrames = useMemo(
        () => TF_ORDER.filter((tf) => timeFrameMap[tf]),
        [timeFrameMap],
    );

    // When a new topic report loads, default the selector to Last Week when available.
    useEffect(() => {
        if (!topicReportData) {
            setActiveTimeFrameType(null);
            return;
        }
        const preferred = defaultTimeFrameType(timeFrameMap);
        setActiveTimeFrameType((prev) => {
            if (prev && timeFrameMap[prev]) return prev;
            return preferred;
        });
    }, [topicReportData, timeFrameMap]);

    // Reset the active time frame when switching reports.
    useEffect(() => {
        setActiveTimeFrameType(null);
    }, [selected?.value]);

    const previewTimeFrame = useMemo(
        () => (activeTimeFrameType ? timeFrameMap[activeTimeFrameType] : null) || null,
        [activeTimeFrameType, timeFrameMap],
    );

    const liveExecutedQueries = useMemo(
        () => normalizeExecutedQueries(previewTimeFrame?.executed_queries),
        [previewTimeFrame?.executed_queries],
    );

    const timeFrameSegments = useMemo(
        () =>
            availableTimeFrames.map((tf) => ({
                id: tf,
                label: (TIME_FRAME_LABEL_KEYS[tf] && t(TIME_FRAME_LABEL_KEYS[tf])) || tf,
            })),
        [availableTimeFrames, t],
    );

    const previewBody = (() => {
        if (!selected) {
            return (
                <div className="h-full flex items-center justify-center text-gray-500 text-sm">
                    {t('reportChat.pickReportPrompt')}
                </div>
            );
        }
        if (selected.kind === 'topic') {
            if (topicLoading) {
                return (
                    <div className="h-full flex items-center justify-center text-gray-500 text-sm">
                        <Loader className="w-5 h-5 animate-spin mr-2 text-primary-600" />
                        {t('reportChat.loadingReport', { label: selected.label })}
                    </div>
                );
            }
            if (topicError) {
                return (
                    <Card padding="base" className="m-6 border-red-200 bg-red-50">
                        <p className="text-sm text-red-800">{topicError}</p>
                    </Card>
                );
            }
            if (!previewTimeFrame) {
                return (
                    <Card padding="base" className="m-6 border-amber-200 bg-amber-50">
                        <p className="text-sm text-amber-900">
                            {t('reportChat.noTimeFrameData')}
                        </p>
                    </Card>
                );
            }
            return (
                <div className="p-6">
                    {timeFrameSegments.length > 1 && (
                        <div className="mb-4 flex items-center gap-3 flex-wrap">
                            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                {t('reportChat.dataRange')}
                            </span>
                            <SegmentedControl
                                items={timeFrameSegments}
                                activeId={activeTimeFrameType}
                                onChange={setActiveTimeFrameType}
                            />
                            {previewTimeFrame.time_frame_label && (
                                <span className="text-xs text-gray-500">
                                    {previewTimeFrame.time_frame_label}
                                </span>
                            )}
                        </div>
                    )}
                    <div key={`topic-${selected.value}-${activeTimeFrameType}-${remountKey}`}>
                        <GeneratedReportDashboard
                            topicKey={selected.topicKey}
                            manifest={manifest ?? undefined}
                            executedQueries={liveExecutedQueries}
                            timeFrameLabel={previewTimeFrame.time_frame_label}
                            dateRange={previewTimeFrame.date_range}
                        />
                    </div>
                </div>
            );
        }
        // overall
        if (overallLoading && overallReports.length === 0) {
            return (
                <div className="h-full flex items-center justify-center text-gray-500 text-sm">
                    <Loader className="w-5 h-5 animate-spin mr-2 text-primary-600" />
                    {t('reportChat.loadingOverallDashboard')}
                </div>
            );
        }
        if (overallReports.length === 0) {
            return (
                <Card padding="base" className="m-6 border-amber-200 bg-amber-50">
                    <p className="text-sm text-amber-900">
                        {t('reportChat.noCompletedReports')}
                    </p>
                </Card>
            );
        }
        return (
            <div key={`overall-${remountKey}`} className="p-6">
                <GeneratedOverallDashboard
                    databaseId={databaseId}
                    manifest={manifest ?? undefined}
                    reports={overallReports}
                    databaseName={databaseName}
                    onOpenPriorities={() => navigate(`/reports/${databaseId}/priorities`)}
                />
            </div>
        );
    })();

    const goBackToReport = () => navigate(`/reports/${databaseId}`);

    return (
        <div className="h-full min-h-0 flex flex-col bg-gray-50">
            {/* Page header */}
            <header className="flex items-center justify-between gap-4 px-6 py-3 bg-white border-b border-gray-200 shrink-0">
                <div className="flex items-center gap-3 min-w-0">
                    <button
                        type="button"
                        onClick={goBackToReport}
                        className="p-2 rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-gray-900 hover:bg-gray-50 hover:border-gray-300 transition-colors shadow-sm shrink-0"
                        aria-label={t('reportChat.backToReport')}
                        title={t('reportChat.backToReport')}
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>

                    <div className="min-w-0">
                        <h1 className="text-base font-bold text-gray-900 truncate">{t('reportChat.title')}</h1>
                        <p className="text-xs text-gray-500 truncate">
                            {databaseName || t('reportChat.loadingDatabase')}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    <DesignChatReportSelector
                        reportOptions={reportOptions}
                        selectedKey={selectedKey}
                        onSelectedKeyChange={handleReportSelect}
                    />
                </div>
            </header>

            {/* Two-column body */}
            <div className="flex-1 flex min-h-0">
                <div className="w-full sm:w-[440px] shrink-0 min-h-0">
                    <DesignChatPanel
                        databaseId={databaseId}
                        selected={selected}
                        mode={chatMode}
                        onChangeMode={setChatMode}
                        onApplied={handleApplied}
                    />
                </div>
                <div className="flex-1 min-w-0 overflow-y-auto bg-gray-50">{previewBody}</div>
            </div>
        </div>
    );
};

export default ReportChat;
